import { useState, useEffect, useCallback } from 'react';
import AuthService from '../../services/AuthService';
import AttendanceService from '../../services/AttendanceService';
import WorkspaceSettingsService from '../../services/WorkspaceSettingsService';
import { distanceMeters, parseLatLng } from '../../../../shared/utils/attendanceGeo';
import {
  ATTENDANCE_STATUSES, LEAVE_TYPES,
  getAttendanceStatus, getApprovalStatus, getLeaveType,
  fmtMinutes, fmtTime, fmtDate,
} from '../../constants/attendanceConstants';

// ─── shared style tokens ──────────────────────────────────────────────────────
const card = { background: '#fff', borderRadius: 16, boxShadow: '0 4px 24px rgba(0,0,0,.08)', padding: 28 };
const badge = (color) => ({
  display: 'inline-block', padding: '3px 12px', borderRadius: 20,
  fontSize: 12, fontWeight: 700, color: '#fff', background: color,
});
const btn = (bg, color = '#fff') => ({
  padding: '10px 24px', borderRadius: 10, border: 'none', cursor: 'pointer',
  background: bg, color, fontWeight: 700, fontSize: 14, transition: 'opacity .2s',
});

export default function MyAttendance() {
  const userId   = AuthService.getLoggedInUserId();
  const clientId = AuthService.getLoggedInClientId();

  const [today, setToday]           = useState(null);
  const [locating, setLocating]     = useState(false);
  const [loading, setLoading]       = useState(false);
  const [toast, setToast]           = useState(null);

  // history
  const [history, setHistory]       = useState([]);
  const [histFrom, setHistFrom]     = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 30); return d.toISOString().slice(0, 10);
  });
  const [histTo, setHistTo]         = useState(new Date().toISOString().slice(0, 10));

  // leave modal
  const [showLeave, setShowLeave]   = useState(false);
  const [leaveForm, setLeaveForm]   = useState({ leaveType: 'Casual', startDate: '', endDate: '', reason: '' });
  const [myLeaves, setMyLeaves]     = useState([]);
  const [geoFence, setGeoFence]     = useState(null);

  const showMsg = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  // ─── load today's record ──────────────────────────────────────────────────
  const loadToday = useCallback(async () => {
    try { setToday(await AttendanceService.getTodayAttendance(userId)); } catch { /* silent */ }
  }, [userId]);

  // ─── load history ─────────────────────────────────────────────────────────
  const loadHistory = useCallback(async () => {
    try {
      const list = await AttendanceService.getMyHistory(userId, new Date(histFrom), new Date(histTo));
      setHistory(list);
    } catch { /* silent */ }
  }, [userId, histFrom, histTo]);

  // ─── load my leave requests ───────────────────────────────────────────────
  const loadLeaves = useCallback(async () => {
    try { setMyLeaves(await AttendanceService.getMyLeaves(userId)); } catch { /* silent */ }
  }, [userId]);

  const loadGeoFence = useCallback(async () => {
    if (!clientId) return;
    try {
      const data = await WorkspaceSettingsService.getAttendanceGeoFence(clientId);
      const enabled =
        data &&
        data.officeLatitude != null &&
        data.officeLongitude != null &&
        data.radiusMeters != null &&
        data.radiusMeters > 0;
      setGeoFence(enabled ? data : null);
    } catch {
      setGeoFence(null);
    }
  }, [clientId]);

  useEffect(() => { loadToday(); loadLeaves(); loadGeoFence(); }, [loadToday, loadLeaves, loadGeoFence]);
  useEffect(() => { loadHistory(); }, [loadHistory]);

  // ─── GPS helper ───────────────────────────────────────────────────────────
  const getLocation = () =>
    new Promise((res) => {
      setLocating(true);
      navigator.geolocation?.getCurrentPosition(
        (p) => { setLocating(false); res(`${p.coords.latitude},${p.coords.longitude}`); },
        () => { setLocating(false); res(''); },
      );
    });

  const validateFenceClient = (locStr) => {
    if (!geoFence?.radiusMeters || geoFence.radiusMeters <= 0) return null;
    const olat = Number(geoFence.officeLatitude);
    const olng = Number(geoFence.officeLongitude);
    if (Number.isNaN(olat) || Number.isNaN(olng)) return null;
    const pos = parseLatLng(locStr);
    if (!pos)
      return 'Attendance area is out of office. Enable location services so we can verify you are at work.';
    const d = distanceMeters(pos.lat, pos.lon, olat, olng);
    if (d > geoFence.radiusMeters)
      return 'Attendance area is out of office. Move within the allowed office radius or contact your administrator.';
    return null;
  };

  const apiErr = (err, fallback) =>
    err?.response?.data?.message || err?.response?.data?.Message || fallback;

  // ─── check-in / check-out ─────────────────────────────────────────────────
  const handleCheckIn = async () => {
    setLoading(true);
    const loc = await getLocation();
    const fenceMsg = validateFenceClient(loc);
    if (fenceMsg) {
      showMsg(fenceMsg, 'error');
      setLoading(false);
      return;
    }
    try {
      const rec = await AttendanceService.checkIn(userId, loc);
      setToday(rec);
      showMsg('Checked in successfully!');
      loadHistory();
    } catch (err) {
      showMsg(apiErr(err, 'Check-in failed.'), 'error');
    }
    setLoading(false);
  };

  const handleCheckOut = async () => {
    setLoading(true);
    const loc = await getLocation();
    const fenceMsg = validateFenceClient(loc);
    if (fenceMsg) {
      showMsg(fenceMsg, 'error');
      setLoading(false);
      return;
    }
    try {
      const rec = await AttendanceService.checkOut(userId, loc);
      setToday(rec);
      showMsg('Checked out. Have a great evening!');
      loadHistory();
    } catch (err) {
      showMsg(apiErr(err, 'Check-out failed.'), 'error');
    }
    setLoading(false);
  };

  // ─── submit leave request ─────────────────────────────────────────────────
  const submitLeave = async () => {
    if (!leaveForm.startDate || !leaveForm.endDate) {
      showMsg('Please fill all fields.', 'error'); return;
    }
    try {
      await AttendanceService.createLeave({ ...leaveForm, userId, clientId });
      showMsg('Leave request submitted!');
      setShowLeave(false);
      setLeaveForm({ leaveType: 'Casual', startDate: '', endDate: '', reason: '' });
      loadLeaves();
    } catch { showMsg('Failed to submit leave.', 'error'); }
  };

  const cancelLeave = async (id) => {
    try {
      await AttendanceService.deleteLeave(id);
      showMsg('Leave cancelled.');
      loadLeaves();
    } catch { showMsg('Failed to cancel.', 'error'); }
  };

  // ─── derived ──────────────────────────────────────────────────────────────
  const checkedIn  = !!today?.checkIn;
  const checkedOut = !!today?.checkOut;
  const todayStatus = today ? getAttendanceStatus(today.status) : null;

  // ─── render ───────────────────────────────────────────────────────────────
  return (
    <div style={{ padding: '24px', background: '#f4f7fa', minHeight: '100vh' }}>

      {/* Toast */}
      {toast && (
        <div style={{
          position: 'fixed', top: 20, right: 20, zIndex: 9999,
          background: toast.type === 'error' ? '#f5365c' : '#2dce89',
          color: '#fff', padding: '14px 28px', borderRadius: 12,
          boxShadow: '0 8px 24px rgba(0,0,0,.2)', fontWeight: 700, fontSize: 15,
        }}>{toast.msg}</div>
      )}

      {/* Header */}
      <div style={{ marginBottom: 28 }}>
        <h3 style={{ margin: 0, fontSize: 26, fontWeight: 800, color: '#1a1f36' }}>
          <i className="fas fa-calendar-check" style={{ color: '#5e72e4', marginRight: 10 }} />
          My Attendance
        </h3>
        <p style={{ color: '#8898aa', margin: '4px 0 0' }}>
          {new Date().toLocaleDateString('en-IN', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' })}
        </p>
      </div>

      {geoFence && (
        <div
          style={{
            marginBottom: 20,
            padding: '14px 18px',
            background: '#fff8e6',
            borderRadius: 12,
            border: '1px solid #ffe8a3',
            fontSize: 13,
            color: '#856404',
            lineHeight: 1.5,
          }}
        >
          <i className="fas fa-map-marked-alt" style={{ marginRight: 8 }} />
          Check-in and check-out must be within <strong>{geoFence.radiusMeters} m</strong> of your office location.
          Outside that area you will see &quot;Attendance area is out of office&quot;.
        </div>
      )}

      {/* Today Card */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(260px,1fr))', gap: 20, marginBottom: 28 }}>
        {/* Status */}
        <div style={{ ...card, background: 'linear-gradient(135deg,#5e72e4,#825ee4)', color: '#fff' }}>
          <p style={{ margin: 0, opacity: .8, fontSize: 13, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}>Today's Status</p>
          <h2 style={{ margin: '10px 0 6px', fontSize: 30 }}>
            {todayStatus ? (
              <><i className={todayStatus.icon} style={{ marginRight: 10 }} />{todayStatus.label}</>
            ) : 'Not Marked'}
          </h2>
          <p style={{ margin: 0, opacity: .8, fontSize: 13 }}>
            {checkedIn ? `Checked in at ${fmtTime(today.checkIn)}` : 'Not yet checked in'}
          </p>
        </div>

        {/* Check-in time */}
        <div style={{ ...card, textAlign: 'center' }}>
          <i className="fas fa-sign-in-alt" style={{ fontSize: 32, color: '#2dce89', marginBottom: 8 }} />
          <p style={{ margin: 0, color: '#8898aa', fontSize: 13, fontWeight: 600 }}>CHECK-IN</p>
          <h3 style={{ margin: '6px 0', color: '#1a1f36' }}>{fmtTime(today?.checkIn)}</h3>
          {today?.locationCheckIn && (
            <p style={{ margin: 0, color: '#adb5bd', fontSize: 11 }}>
              <i className="fas fa-map-marker-alt" style={{ marginRight: 4 }} />
              {today.locationCheckIn.length > 30 ? `${today.locationCheckIn.slice(0, 30)}…` : today.locationCheckIn}
            </p>
          )}
        </div>

        {/* Check-out time */}
        <div style={{ ...card, textAlign: 'center' }}>
          <i className="fas fa-sign-out-alt" style={{ fontSize: 32, color: '#f5365c', marginBottom: 8 }} />
          <p style={{ margin: 0, color: '#8898aa', fontSize: 13, fontWeight: 600 }}>CHECK-OUT</p>
          <h3 style={{ margin: '6px 0', color: '#1a1f36' }}>{fmtTime(today?.checkOut)}</h3>
          {today?.locationCheckOut && (
            <p style={{ margin: 0, color: '#adb5bd', fontSize: 11 }}>
              <i className="fas fa-map-marker-alt" style={{ marginRight: 4 }} />
              {today.locationCheckOut.length > 30 ? `${today.locationCheckOut.slice(0, 30)}…` : today.locationCheckOut}
            </p>
          )}
        </div>

        {/* Working hours */}
        <div style={{ ...card, textAlign: 'center' }}>
          <i className="fas fa-clock" style={{ fontSize: 32, color: '#fb6340', marginBottom: 8 }} />
          <p style={{ margin: 0, color: '#8898aa', fontSize: 13, fontWeight: 600 }}>WORKING HOURS</p>
          <h3 style={{ margin: '6px 0', color: '#1a1f36' }}>{fmtMinutes(today?.workingMinutes)}</h3>
          <p style={{ margin: 0, color: '#adb5bd', fontSize: 12 }}>
            {today?.workingMinutes >= 540 ? '✅ Full Day' : today?.workingMinutes > 0 ? '⏳ In Progress' : 'Not started'}
          </p>
        </div>
      </div>

      {/* Action Buttons */}
      <div style={{ ...card, marginBottom: 28, display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'center' }}>
        <h5 style={{ margin: 0, color: '#1a1f36', flex: 1, fontWeight: 700 }}>
          <i className="fas fa-bolt" style={{ color: '#f5a623', marginRight: 8 }} />Quick Actions
        </h5>

        {!checkedIn && (
          <button style={btn('#2dce89')} onClick={handleCheckIn} disabled={loading || locating}>
            <i className="fas fa-sign-in-alt" style={{ marginRight: 7 }} />
            {locating ? 'Getting location…' : loading ? 'Checking in…' : 'Start Day (Check In)'}
          </button>
        )}

        {checkedIn && !checkedOut && (
          <button style={btn('#f5365c')} onClick={handleCheckOut} disabled={loading || locating}>
            <i className="fas fa-sign-out-alt" style={{ marginRight: 7 }} />
            {locating ? 'Getting location…' : loading ? 'Checking out…' : 'End Day (Check Out)'}
          </button>
        )}

        {checkedOut && (
          <span style={{ ...badge('#2dce89'), fontSize: 14, padding: '8px 20px' }}>
            <i className="fas fa-check-double" style={{ marginRight: 6 }} />Day Complete
          </span>
        )}

        <button style={btn('#5e72e4')} onClick={() => setShowLeave(true)}>
          <i className="fas fa-umbrella-beach" style={{ marginRight: 7 }} />Apply for Leave
        </button>
      </div>

      {/* Leave Request Modal */}
      {showLeave && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', zIndex: 1000,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <div style={{ ...card, width: '100%', maxWidth: 480, position: 'relative' }}>
            <button onClick={() => setShowLeave(false)} style={{
              position: 'absolute', top: 14, right: 14, background: 'none',
              border: 'none', fontSize: 22, cursor: 'pointer', color: '#8898aa',
            }}>×</button>
            <h4 style={{ margin: '0 0 20px', color: '#1a1f36' }}>
              <i className="fas fa-calendar-plus" style={{ color: '#5e72e4', marginRight: 8 }} />Apply for Leave
            </h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div>
                <label style={{ fontSize: 12, fontWeight: 700, color: '#8898aa', textTransform: 'uppercase' }}>Leave Type</label>
                <select value={leaveForm.leaveType} onChange={(e) => setLeaveForm({ ...leaveForm, leaveType: e.target.value })}
                  style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid #e3e6ec', marginTop: 4 }}>
                  {LEAVE_TYPES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
                </select>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 700, color: '#8898aa', textTransform: 'uppercase' }}>From</label>
                  <input type="date" value={leaveForm.startDate}
                    onChange={(e) => setLeaveForm({ ...leaveForm, startDate: e.target.value })}
                    style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid #e3e6ec', marginTop: 4, boxSizing: 'border-box' }} />
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 700, color: '#8898aa', textTransform: 'uppercase' }}>To</label>
                  <input type="date" value={leaveForm.endDate}
                    onChange={(e) => setLeaveForm({ ...leaveForm, endDate: e.target.value })}
                    style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid #e3e6ec', marginTop: 4, boxSizing: 'border-box' }} />
                </div>
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 700, color: '#8898aa', textTransform: 'uppercase' }}>Reason</label>
                <textarea value={leaveForm.reason}
                  onChange={(e) => setLeaveForm({ ...leaveForm, reason: e.target.value })}
                  rows={3} placeholder="Brief reason for leave…"
                  style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid #e3e6ec', marginTop: 4, resize: 'vertical', boxSizing: 'border-box' }} />
              </div>
              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 4 }}>
                <button style={btn('#e3e6ec', '#1a1f36')} onClick={() => setShowLeave(false)}>Cancel</button>
                <button style={btn('#5e72e4')} onClick={submitLeave}>Submit Request</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* My Leave Requests */}
      <div style={{ ...card, marginBottom: 28 }}>
        <h5 style={{ margin: '0 0 18px', color: '#1a1f36', fontWeight: 700 }}>
          <i className="fas fa-list-alt" style={{ color: '#11cdef', marginRight: 8 }} />My Leave Requests
        </h5>
        {myLeaves.length === 0 ? (
          <p style={{ color: '#adb5bd', textAlign: 'center', margin: '24px 0' }}>No leave requests yet.</p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
              <thead>
                <tr style={{ background: '#f4f7fa' }}>
                  {['Type', 'From', 'To', 'Reason', 'Status', 'Action'].map((h) => (
                    <th key={h} style={{ padding: '10px 14px', textAlign: 'left', color: '#8898aa', fontSize: 12, fontWeight: 700, textTransform: 'uppercase' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {myLeaves.map((l) => {
                  const ap = getApprovalStatus(l.approvalStatus);
                  return (
                    <tr key={l.id} style={{ borderBottom: '1px solid #f0f2f5' }}>
                      <td style={{ padding: '12px 14px', fontWeight: 600 }}>{getLeaveType(l.leaveType)}</td>
                      <td style={{ padding: '12px 14px' }}>{fmtDate(l.startDate)}</td>
                      <td style={{ padding: '12px 14px' }}>{fmtDate(l.endDate)}</td>
                      <td style={{ padding: '12px 14px', color: '#8898aa', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{l.reason || '—'}</td>
                      <td style={{ padding: '12px 14px' }}><span style={badge(ap.color)}>{ap.label}</span></td>
                      <td style={{ padding: '12px 14px' }}>
                        {l.approvalStatus === 'Pending' && (
                          <button onClick={() => cancelLeave(l.id)}
                            style={{ background: 'none', border: '1px solid #f5365c', color: '#f5365c', borderRadius: 6, padding: '4px 12px', cursor: 'pointer', fontSize: 12 }}>
                            Cancel
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Attendance History */}
      <div style={card}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 18, flexWrap: 'wrap' }}>
          <h5 style={{ margin: 0, color: '#1a1f36', fontWeight: 700, flex: 1 }}>
            <i className="fas fa-history" style={{ color: '#825ee4', marginRight: 8 }} />Attendance History
          </h5>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <input type="date" value={histFrom} onChange={(e) => setHistFrom(e.target.value)}
              style={{ padding: '7px 12px', borderRadius: 8, border: '1px solid #e3e6ec', fontSize: 13 }} />
            <span style={{ color: '#8898aa' }}>to</span>
            <input type="date" value={histTo} onChange={(e) => setHistTo(e.target.value)}
              style={{ padding: '7px 12px', borderRadius: 8, border: '1px solid #e3e6ec', fontSize: 13 }} />
          </div>
        </div>

        {/* Summary chips */}
        <div style={{ display: 'flex', gap: 10, marginBottom: 18, flexWrap: 'wrap' }}>
          {ATTENDANCE_STATUSES.map((s) => {
            const count = history.filter((h) => h.status === s.id).length;
            if (!count) return null;
            return (
              <span key={s.id} style={{
                background: `${s.color}18`, color: s.color, border: `1px solid ${s.color}44`,
                borderRadius: 20, padding: '4px 14px', fontSize: 13, fontWeight: 700,
              }}>
                <i className={s.icon} style={{ marginRight: 5 }} />{s.label}: {count}
              </span>
            );
          })}
        </div>

        {history.length === 0 ? (
          <p style={{ color: '#adb5bd', textAlign: 'center', margin: '32px 0' }}>No records in this range.</p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
              <thead>
                <tr style={{ background: '#f4f7fa' }}>
                  {['Date', 'Status', 'Check In', 'Check Out', 'Hours', 'Location'].map((h) => (
                    <th key={h} style={{ padding: '10px 14px', textAlign: 'left', color: '#8898aa', fontSize: 12, fontWeight: 700, textTransform: 'uppercase' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {history.map((row) => {
                  const st = getAttendanceStatus(row.status);
                  return (
                    <tr key={row.id} style={{ borderBottom: '1px solid #f0f2f5' }}>
                      <td style={{ padding: '12px 14px', fontWeight: 600 }}>{fmtDate(row.workDate)}</td>
                      <td style={{ padding: '12px 14px' }}>
                        <span style={badge(st.color)}>
                          <i className={st.icon} style={{ marginRight: 4 }} />{st.label}
                        </span>
                      </td>
                      <td style={{ padding: '12px 14px' }}>{fmtTime(row.checkIn)}</td>
                      <td style={{ padding: '12px 14px' }}>{fmtTime(row.checkOut)}</td>
                      <td style={{ padding: '12px 14px', fontWeight: 600, color: row.workingMinutes >= 480 ? '#2dce89' : '#fb6340' }}>
                        {fmtMinutes(row.workingMinutes)}
                      </td>
                      <td style={{ padding: '12px 14px', color: '#adb5bd', fontSize: 12 }}>
                        {row.locationCheckIn ? (
                          <a href={`https://maps.google.com/?q=${row.locationCheckIn}`} target="_blank" rel="noreferrer"
                            style={{ color: '#5e72e4' }}>
                            <i className="fas fa-map-marker-alt" /> View
                          </a>
                        ) : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
